/* eslint-disable max-lines */
import OrderApi from "../../../../services/api/orderApi.js";
import KeysApi from "../../../../services/api/keysApi.js";
import paymentTerms from "../../paymentTerms/paymentTerms.vue";
import Vue from "vue";
import trackEvent from "@/services/tracking";
import { mapGetters } from "vuex";
import { checkoutHelper } from "@/components/mixin/checkoutHelper";

const OrderApiService = new OrderApi(),
  KeysApiService = new KeysApi();

export default {
  mixins: [checkoutHelper],
  components: {
    paymentTerms,
  },
  data() {
    return {
      no_cards: false,
      save_card: false,
      make_default: false,
      request_in_progress: false,
      display: "loading",
      cards: [],
      selected_card: null,
      show_undo: false,
      payOnShipment: null,
      default_disabled: false,
    };
  },

  mounted() {
    this.address = this.$store.getters.checkoutState.shipping;

    if (this.cart) {
      this.payOnShipment = this.cart.payment_terms === "NET_0";
    }
  },

  methods: {
    selectCard,
    cardsLoaded,
    initializeStripeElement,
    handleToggleClick,
    submitCard,
    resetBrandCheckout,
    trackCreateOrder,
    submit,
    showPurchaseInProgressModal,
    showPaymentTermsHelpModal,
  },

  computed: {
    ...mapGetters({
      user: "userState",
    }),
    cart() {
      return this.$store.getters.checkoutState.cart;
    },
    stripe() {
      return this.$store.state.keysState.stripe_api_key && Stripe(this.$store.state.keysState.stripe_api_key);
    },
    isPromoCodeLoading() {
      return this.$store.getters.checkoutState.isPromoCodeLoading;
    },
  },

  watch: {
    payOnShipment(val) {
      if (this.cart) {
        this.$emit("net0-choice-change", this.cart.payment_terms === "NET_60" && val);
      }
    },
  },
};

function selectCard(data) {
  this.selected_card = data;
  this.default_disabled = this.selected_card.is_default;
  this.make_default = false;
}

function cardsLoaded({ cards, selectedCard }) {
  this.cards = cards;
  if (cards?.length) {
    this.no_cards = false;
    this.display = "stored_card";
  } else {
    this.no_cards = true;
    this.save_card = true;
    this.display = "stripe_input_form";
    this.initializeStripeElement();
  }

  if (selectedCard) {
    this.selectCard(selectedCard);
  }
}

async function initializeStripeElement() {
  if (!this.stripe) {
    try {
      const { data } = await KeysApiService.getClientKeys();
      this.$store.commit("addKeysToKeysState", data);
    } catch (error) {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$logger.notify(new Error(error));
      }
    }
  }
  const elements = this.stripe.elements(),
    card = elements.create("card", {
      style: {
        base: {
          color: "#32325d",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          fontSmoothing: "antialiased",
          fontSize: "16px",
          "::placeholder": {
            color: "#aab7c4",
          },
          ":-webkit-autofill": {
            color: "#32325d",
          },
        },
        invalid: {
          color: "#fa755a",
          iconColor: "#fa755a",
          ":-webkit-autofill": {
            color: "#fa755a",
          },
        },
      },
    });
  await this.$nextTick();
  card.mount("#card-element");
  this.card = card;
}

function handleToggleClick() {
  this.display = this.display === "stripe_input_form" ? "stored_card" : "stripe_input_form";
  this.show_undo = !this.show_undo;
  this.display === "stripe_input_form" && this.initializeStripeElement();
}

async function submit() {
  await this.saveAddresses();
  this.submitCard();
}

async function submitCard() {
  const _self = this,
    request = {};
  _self.request_in_progress = true;
  if (!_self.address) {
    _self.$swal("Attention", "Please provide shipping address");
    _self.request_in_progress = false;
    return;
  }

  if (!_self.selected_card && !_self.no_cards) {
    _self.$swal("Attention", "Please make sure to select your credit card info");
    _self.request_in_progress = false;
    return;
  }

  // add shipping configuration to request
  if (_self.address.different_billing) {
    request.use_shipping_as_billing = false;
  } else {
    request.use_shipping_as_billing = true;
  }
  if (_self.address.selected_shipping_address && _self.address.selected_shipping_address.id) {
    request.saved_shipping_address_id = _self.address.selected_shipping_address.id;
  } else {
    request.shipping_address = _self.address.model.shipping_address;
  }
  if (_self.address.different_billing) {
    if (_self.address.selected_billing_address && _self.address.selected_billing_address.id) {
      request.saved_billing_address_id = _self.address.selected_billing_address.id;
    } else {
      request.billing_address = _self.address.model.billing_address;
    }
  }

  // add card storage configuration
  if (this.display === "stripe_input_form") {
    const { token, error } = await this.stripe.createToken(this.card);
    if (error) {
      const errorElement = document.getElementById("card-errors");
      errorElement.textContent = error.message;
      _self.request_in_progress = false;
      return;
    }

    request.token_id = token.id;
    request.use_saved_card = false;
    request.save_card = this.save_card;
  }

  if (this.display === "stored_card") {
    request.use_saved_card = true;
    request.save_card = false;
    request.card_id = this.selected_card.value;
  }

  this.showPurchaseInProgressModal();
  request.make_default = this.make_default;
  request.pay_on_shipment = this.payOnShipment;

  // included promo codeds in request
  // eslint-disable-next-line one-var
  const promoCodes = [];
  this.cart.sub_carts.forEach((cart) => {
    if (cart.promo_code) {
      promoCodes.push(cart.promo_code);
    }
  });

  if (promoCodes.length) {
    request.promo_codes = promoCodes;
  }
  try {
    const { data } = await OrderApiService.createOrder(request);
    this.trackCreateOrder(request, data.id);
    _self.$router.push(`/checkout/order-confirmation/${data.id}`);
    Vue.swal.close();
  } catch (error) {
    _self.request_in_progress = false;
    Vue.swal.close();
    if (error.response) {
      _self.$swal("Attention", error.response.data.error_message);
    } else {
      _self.$logger.notify(error);
    }
  }
  this.resetBrandCheckout();
}

function resetBrandCheckout() {
  OrderApiService.checkoutBrand(null).catch((error) => this.$swal("Attention", error.response.data.error_message));
}

function trackCreateOrder(request, orderId) {
  this.$logger.addAction("trackCreateOrder", {
    request,
  });
  this.$ga.event({
    eventCategory: "checkout",
    eventAction: "createOrderSuccess",
  });
  const subCarts = this.cart.sub_carts.filter((cart) => cart.selected_for_checkout),
    orderData = {
      "Product Quantity": subCarts.reduce((accumulator, current) => accumulator + current.cart_items.length),
      "Total Value": this.cart.total,
      "Product Ids": subCarts.flatMap((sub) => sub.cart_items.map((item) => item.product_id)),
      "Product Names": subCarts.flatMap((sub) => sub.cart_items.map((item) => item.product_name)),
      "Brand Names": subCarts.map((cart) => cart.brand_info.brand_name),
      "Promo Code Used": subCarts.map((cart) => cart.promo_code),
      "Promo Code": subCarts.map((cart) => cart.promo_code),
      "Promo Code Ammount": subCarts.map((cart) => cart.promo_code_amount),
    };
  subCarts.forEach((cart) => {
    cart.cart_items.forEach((item) => {
      this.$ma.trackEvent({
        action: "Product Ordered",
        properties: {
          "Category Name": item.category_name,
          Quantity: item.quantity,
          "Product Name": item.product_name,
          "Variant name": item.variant_name,
        },
      });
    });
  });
  this.$ma.trackEvent({
    action: "Order Created",
    properties: orderData,
  });
  mixpanel.people.increment("Lifetime Order Request Value", this.cart.total);
  mixpanel.people.increment("# of Orders Placed");
  mixpanel.people.increment(
    "Total Quantity of Items Purchased",
    subCarts.flatMap((cart) => cart.cart_items.map((item) => item.quantity)).reduce((total, current) => total + current)
  );

  mixpanel.people.increment(
    "Lifetime # of Promo Codes Used",
    subCarts.map((cart) => cart.promo_code).reduce((total, current) => total + current)
  );

  // facebook tracking
  if (this.$store.getters.isRetailerApproved) {
    trackEvent(
      "Purchase",
      {
        currency: "USD",
        // eslint-disable-next-line no-magic-numbers
        value: this.cart.total.toFixed(2),
      },
      {
        eventID: orderId,
      }
    );
  }
}

function showPaymentTermsHelpModal() {
  this.$swal(
    "Payment Terms",
    `
        <b>Pay on Shipment</b>: Charged when your products are shipped. <br>
        <b>Net 60 terms</b>: Charged in 60 days from when your products are shipped
    `
  );
}

function showPurchaseInProgressModal() {
  this.$swal({
    title: "Ooooh! Nice Picks",
    text: "Hang tight while we secure your items... and by the way, you've got great taste!",
    showConfirmButton: false,
    showCloseButton: false,
    allowEscapeKey: false,
  });
}
